import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { LinkOutlined } from "@ant-design/icons";
import { LEANCUSTOMS_ORDER_BASE } from "../../constants/leancustoms";
import { formatLeanCustomsOrderDetailLine } from "../../utils/leanCustomsOrderDisplay";

type LeanOrderRow = Record<string, unknown> & { id?: string };

const orderColumns: ColumnsType<LeanOrderRow> = [
  {
    title: "Reference",
    key: "reference",
    render: (_, o) => {
      const bn = o["booking_number"];
      const bnStr =
        typeof bn === "string" || typeof bn === "number" ? String(bn) : "";
      const ref = typeof o.reference === "string" ? o.reference : "";
      const text = ref || bnStr || "—";
      return (
        <Typography.Text code style={{ fontSize: 12 }}>
          {text}
        </Typography.Text>
      );
    },
  },
  {
    title: "Order ID",
    key: "id",
    width: 130,
    ellipsis: true,
    render: (_, o) => {
      const id = typeof o.id === "string" ? o.id : "";
      return id ? (
        <Typography.Text code copyable={{ text: id }} style={{ fontSize: 12 }}>
          {id}
        </Typography.Text>
      ) : (
        "—"
      );
    },
  },
  {
    title: "Details",
    key: "details",
    width: 180,
    ellipsis: true,
    render: (_, o) => formatLeanCustomsOrderDetailLine(o) ?? "—",
  },
  {
    title: "Updated",
    key: "updated_at",
    width: 132,
    render: (_, o) => {
      const t = o.updated_at ?? o.created_at;
      return t && typeof t === "string" ? dayjs(t).format("YYYY-MM-DD HH:mm") : "—";
    },
  },
  {
    title: "Open",
    key: "open_order",
    width: 72,
    render: (_, o) => {
      const id = typeof o.id === "string" ? o.id : "";
      return id ? (
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          href={`${LEANCUSTOMS_ORDER_BASE}/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ padding: 0, height: "auto" }}
        >
          LC
        </Button>
      ) : null;
    },
  },
];

export interface BookingCustomsLeanMatchesPanelProps {
  bookingId: number;
  bookingReference: string;
  orders: LeanOrderRow[];
  loading: boolean;
  linking: boolean;
  existingOrderId?: string | null;
  onLinkOrder: (orderId: string) => void | Promise<void>;
}

/** Lean Customs order matches for this booking (EFF reference). */
export const BookingCustomsLeanMatchesPanel: React.FC<
  BookingCustomsLeanMatchesPanelProps
> = ({ bookingId, bookingReference, orders, loading, linking, existingOrderId, onLinkOrder }) => {
  const { token } = theme.useToken();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  useEffect(() => {
    setSelectedOrderId((prev) => {
      const ids = new Set(
        orders.map((o) => (typeof o.id === "string" ? o.id : "")).filter(Boolean)
      );
      if (prev && ids.has(prev)) return prev;
      const first = orders.find((o) => typeof o.id === "string" && o.id);
      return typeof first?.id === "string" ? first.id : null;
    });
  }, [orders]);

  const orderFound = orders.length > 0;
  const orderAlreadyLinked =
    !!selectedOrderId &&
    !!existingOrderId &&
    String(selectedOrderId) === String(existingOrderId);
  const canLink = !loading && orderFound && !!selectedOrderId && !orderAlreadyLinked;

  const cardStyle = useMemo(
    () => ({
      borderColor: orderFound ? token.colorSuccess : token.colorWarning,
    }),
    [orderFound, token.colorSuccess, token.colorWarning]
  );

  if (loading) {
    return <Spin size="small" />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {orderFound && (
        <Alert
          type="warning"
          showIcon
          message="Matching Lean Customs order"
          description={
            <>
              Found an order in Lean Customs for reference{" "}
              <Typography.Text code>{bookingReference}</Typography.Text> (
              <Typography.Text code>EFF{bookingId}</Typography.Text>), but this booking does not
              have an order ID saved yet. Select a row and use <strong>Link order to booking</strong>,
              or <strong>Create order in LeanCustoms</strong> for a new order.
            </>
          }
        />
      )}

      <Card
        size="small"
        title={
          <Space size={8} wrap>
            <span>Order</span>
            <Tag color={orderFound ? "success" : "warning"}>
              {orderFound ? "Match found" : "No match"}
            </Tag>
          </Space>
        }
        styles={{ body: { padding: 12 } }}
        style={cardStyle}
      >
        {orderFound ? (
          <Table<LeanOrderRow>
            size="small"
            rowKey={(r, i) =>
              typeof r.id === "string" && r.id ? r.id : `lc-order-${i}`
            }
            columns={orderColumns}
            dataSource={orders}
            pagination={
              orders.length > 6
                ? { pageSize: 6, size: "small", showSizeChanger: false }
                : false
            }
            scroll={{ x: "max-content" }}
            rowSelection={{
              type: "radio",
              selectedRowKeys: selectedOrderId ? [selectedOrderId] : [],
              onChange: (keys) => {
                const k = keys[0];
                setSelectedOrderId(typeof k === "string" ? k : null);
              },
            }}
          />
        ) : (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            No order matched <Typography.Text code>{bookingReference}</Typography.Text> in this
            search.
          </Typography.Paragraph>
        )}
      </Card>

      {orderFound && (
        <>
          <Button
            type="default"
            disabled={!canLink}
            loading={linking}
            onClick={() => {
              if (selectedOrderId) void onLinkOrder(selectedOrderId);
            }}
          >
            Link order to booking
          </Button>
          {!canLink && orderAlreadyLinked && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This order is already linked to the booking.
            </Typography.Text>
          )}
        </>
      )}
    </Space>
  );
};
